export interface IFieldErrors {
  policeService: boolean;
  operationalArea: boolean;
  occurrenceNumbers: boolean;
  incidentTimes: boolean;
  type: boolean;
  associatedCrimes: boolean;
  description: boolean;
  addresses: {
    address: any;
    isValid: boolean;
    hasLocationType: boolean;
  }[];
  contactEmail: boolean;
  contactPhone: boolean;
  contactName: boolean[];
  classification: boolean;
}

import {
  stripPhoneNumberFormatting,
  validateEmail,
  validatePhone,
} from "@/common/utils";
import { IAlertGroup, IAlertProperties, IContact, PlaceType } from "@/types";

import {
  validateContactName,
  validateGeocodedAddresses,
  validateOperationalArea,
} from "./utils";

export enum ALERT_STEP {
  DETAILS = "details",
  UPLOAD = "upload",
  REVIEW = "review",
}

const { DETAILS, UPLOAD, REVIEW } = ALERT_STEP;

export const alertSteps = [DETAILS, UPLOAD, REVIEW];

export const emptyContact: IContact = {
  email: "",
  phone_number: "",
  extension: "",
  name: "",
  badge_number: "",
  error: false,
};

export enum ALERT_CLASSIFICATION {
  PROTECTED_A = "protected_a",
  PROTECTED_B = "protected_b",
  UNCLASSIFIED = "unclassified",
}

export enum LOCATION_CLASSIFICATION {
  INCIDENT_ADDRESS = "Incident Address",
  HOME_ADDRESS = "Home Address",
  WORK_ADDRESS = "Work Address",
  FREQUENTED_ADDRESS = "Frequented Address",
  LAST_SEEN_ADDRESS = "Last Seen Address",
  SAFE_HOUSE_ADDRESS = "Safe House Address",
  SURETY_ADDRESS = "Surety Address",
  SCHOOL_ADDRESS = "School Address",
  HIDEOUT_ADDRESS = "Hideout Address",
  VICTIM_ADDRESS = "Victim Address",
  CONTACT_ADDRESS = "Contact Address",
  ARREST_ADDRESS = "Arrest Address",
  OTHER = "Other",
}

export const LOCATION_CLASSIFICATION_REFERENCE: Record<
  LOCATION_CLASSIFICATION,
  string
> = {
  "Incident Address": "incident_address",
  "Home Address": "home_address",
  "Work Address": "work_address",
  "Frequented Address": "frequent_address",
  "Last Seen Address": "last_seen_address",
  "Safe House Address": "safe_house_address",
  "Surety Address": "surety_address",
  "School Address": "school_address",
  "Hideout Address": "hideout_address",
  "Victim Address": "victim_address",
  "Contact Address": "contact_address",
  "Arrest Address": "arrest_address",
  Other: "other",
};

export enum USER_TYPE {
  INDEPENDENT = "independent",
  REGULAR = "regular",
}

export const defaultAlertProperties: IAlertProperties = {
  policeService: null,
  occurrenceNumbers: [],
  type: null,
  associatedCrimes: [],
  flags: [],
  incidentTimes: [{ start_date: null, end_date: null, time_type: "exact" }],
  description: null,
  descriptionFr: null,
  originalLanguage: null,
  tagDict: {},
  entities: [],
  issuedTime: null,
  operationalArea: null,
  approvedBy: null,
  approvedByBadge: null,
  classification: ALERT_CLASSIFICATION.PROTECTED_A,
  contacts: [emptyContact],
  rawAddresses: [null] as unknown as PlaceType[],
  evidence_files: {},
  isDraft: true,
  pdf: {},
  resolutions: [],
};

export enum ALERT_STATUS {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
}
export const generateValidationCriteria = async (
  alertProperties: IAlertProperties,
  policeServices: IAlertGroup[] = [],
) => {
  const {
    policeService,
    operationalArea,
    occurrenceNumbers,
    type,
    associatedCrimes,
    description,
    contacts,
    rawAddresses,
    classification,
  } = alertProperties;

  const addressErrors: any[] = [];
  const res = await validateGeocodedAddresses(rawAddresses);
  res.forEach((address) => {
    addressErrors.push(address);
  });

  const validateContactEmail = (contact) => {
    if (contact.email === "" && contact.phone_number === "") {
      return true;
    } else if (contact.email !== "" && !validateEmail(contact.email)) {
      return true;
    } else {
      return false;
    }
  };

  const validateContactPhone = (contact) => {
    const strippedPhone = stripPhoneNumberFormatting(contact?.phone_number);
    if (contact.phone_number === "" && contact.email === "") {
      return true;
    } else if (contact.phone_number !== "" && !validatePhone(strippedPhone)) {
      return true;
    }
    return false;
  };
  const validationMap: IFieldErrors = {
    policeService: policeService ? false : true,
    operationalArea: policeService
      ? validateOperationalArea(
          policeServices.find((ps) => ps.id === policeService.id) ??
            policeService,
          operationalArea,
        )
      : false,

    occurrenceNumbers: occurrenceNumbers.length === 0 ? true : false,
    classification:
      classification === ("" as ALERT_CLASSIFICATION) ||
      classification === null ||
      classification === undefined
        ? true
        : false,
    // incidentDate:
    //   incidentDate === null ||
    //   !incidentDate.isValid() ||
    //   incidentDate.isAfter(new Date())
    //     ? true
    //     : false,
    type: type ? false : true,
    incidentTimes: true,
    associatedCrimes: associatedCrimes.length === 0 ? true : false,
    description: description === "" || description === null ? true : false,
    addresses: addressErrors,
    contactEmail: contacts[0] ? validateContactEmail(contacts[0]) : false,
    contactPhone: contacts[0] ? validateContactPhone(contacts[0]) : false,
    contactName: validateContactName(contacts),
  };
  return validationMap;
};

export const defaultErrors: IFieldErrors = {
  policeService: false,
  operationalArea: false,
  occurrenceNumbers: false,
  incidentTimes: false,
  type: false,
  associatedCrimes: false,
  description: false,
  addresses: [],
  contactEmail: false,
  contactPhone: false,
  classification: false,
  contactName: [],
};

export enum BULLETIN_TYPE {
  "arrests_made" = "arrests_made",
  "bail_compliance" = "bail_compliance",
  "drug_alert" = "drug_alert",
  "focus_patrol" = "focus_patrol",
  "missing_person" = "missing_person",
  "officer_safety" = "officer_safety",
  "police_information" = "police_information",
  "property_to_locate" = "property_to_locate",
  "suspect_to_identify" = "suspect_to_identify",
  "suspect_and_vehicle_to_identify" = "suspect_and_vehicle_to_identify",
  "vehicle_to_identify" = "vehicle_to_identify",
  "warrant" = "warrant",
  "warrant_request" = "warrant_request",
  "wellness_check" = "wellness_check",
  "other" = "other",
}

export const TIME_TYPE = {
  EXACT: "exact",
  DATE_ONLY: "time_unknown",
  RANGE: "range",
  NOT_APPLICABLE: "not_applicable",
  UNKNOWN: "unknown",
};

export const BULLETIN_PRIORITIES = ["low", "fyi", "medium", "high"] as const;

export type BulletinPriority = (typeof BULLETIN_PRIORITIES)[number];

// Decorative pastel accents, rendered by MediaCard at alpha(colour, 0.25) so
// they read as a tint in either colour mode. No equivalent in the shared
// @rubi/ui palette, so these stay local, at their pre-swap hexes. Do NOT map them onto action.* states: alpha()
// replaces the alpha channel, so translucent tokens collapse into each other.
const BULLETIN_PRIORITY_COLORS: Record<BulletinPriority, string> = {
  low: "#aec6cf",
  fyi: "#77dd77",
  medium: "#fffacd",
  high: "#ff6961",
};

export const BULLETIN_PRIORITY_LABELS: Record<BulletinPriority, string> = {
  low: "Low",
  fyi: "FYI",
  medium: "Medium",
  high: "High",
};

export const getBulletinPriorityColor = (
  priority: string | null | undefined,
): string =>
  priority && priority in BULLETIN_PRIORITY_COLORS
    ? BULLETIN_PRIORITY_COLORS[priority as BulletinPriority]
    : "";
